import type { JsonValue } from '@protobuf-ts/runtime';
import { uniqueId } from 'lodash-es';
import type { ReadonlyDeep, ValueOf } from 'type-fest';

import { Timestamp } from '@viamrobotics/web-client/google/protobuf/timestamp';

import { MingoUpdateOperators } from '$lib/mingo';
import {
  ConfigErrorTypes,
  type ConfigSemanticError,
  type ConfigSyntaxError,
} from '$lib/parse-config';
import type { CaptureMethodName } from '$lib/rdk';
import type { APITriplet, ModelTriplet } from '$lib/resource-type';

import type { Frame, Notes } from './spatial';
import type { TriggerTypesMap } from './trigger';
import type { ResourceLogLevel } from './log-configuration';

export const PartTypes = {
  MAIN: 'main-part',
  SUB: 'sub-part',
} as const;

export type PartType = ValueOf<typeof PartTypes>;

export const ResourceTypes = {
  COMPONENT: 'component',
  SERVICE: 'service',
  REGISTRY_MODULE: 'registry-module',
  LOCAL_MODULE: 'local-module',
  REMOTE: 'remote',
  PROCESS: 'process',
  PACKAGE: 'package',
  JOB: 'job',
  TRIGGER: 'trigger',
  FRAGMENT_IMPORT: 'fragment-import',
} as const;

export type ResourceType = ValueOf<typeof ResourceTypes>;

export type ComponentOrServiceType =
  | typeof ResourceTypes.COMPONENT
  | typeof ResourceTypes.SERVICE;

export const SingletonResourceTypes = {
  AGENT: 'agent',
  NETWORK: 'network',
  MAINTENANCE: 'maintenance',
  BILLING: 'billing',
} as const;

export type SingletonResourceType = ValueOf<typeof SingletonResourceTypes>;

export const ConfigItemTypes = {
  ...ResourceTypes,
  ...SingletonResourceTypes,
} as const;

export type ConfigItemType = ValueOf<typeof ConfigItemTypes>;

// TODO(mc, 2023-01-31): replace with opaque types
export type PartID = string;
export type ResourceID = string;
export type FragmentID = string;
export type FragmentPrefix = string;

export type FragmentLookup = Readonly<
  Record<FragmentID, Readonly<Record<FragmentPrefix, ReadonlyFragment>>>
>;

export const partID = (rawPartID: string): PartID => rawPartID;

/**
 * Create a stable ID for a resource, based on its parent, type, and name.
 *
 * This ID creator is safe to use when all resources are completely known,
 * and we can guarantee uniqueness. Use it when replacing a full part config.
 */
export const stableResourceID = (
  parentPartID: PartID,
  resourceType: ResourceType,
  resourceName: string,
  index: number
) =>
  [parentPartID, resourceType, encodeURIComponent(resourceName), index].join(
    '/'
  );

/**
 * Create a "random" ID for a newly added resource.
 *
 * Use when an ID from `resourceID` risks collision
 * due to, for example, unsaved resource renames.
 * Use it for newly created resources added via the UI.
 */
export const randomResourceID = (resourceType: ResourceType) =>
  uniqueId(resourceType);

export type ReadonlyNamedResource =
  | ReadonlyComponent
  | ReadonlyService
  | ReadonlyRegistryModule
  | ReadonlyLocalModule
  | ReadonlyRemote
  | ReadonlyProcess
  | ReadonlyPackage
  | ReadonlyTrigger
  | ReadonlyJob;

export type ReadonlyResource = ReadonlyNamedResource | ReadonlyFragmentImport;

export type ReadonlyResources = readonly ReadonlyResource[];

export type ConfigItem = ReadonlyMaybePart | ReadonlyResource;

export type ConfigItemToRender = ReadonlyMaybePart | ReadonlyResourceToRender;

export type ReadonlyResourceToRender = Exclude<
  ReadonlyResource,
  ReadonlyPackage
>;

export const isResourceToRender = (
  resource: ConfigItem
): resource is ReadonlyResourceToRender =>
  isComponent(resource) ||
  isService(resource) ||
  isRegistryModule(resource) ||
  isLocalModule(resource) ||
  isRemote(resource) ||
  isProcess(resource) ||
  isTrigger(resource) ||
  isJob(resource) ||
  isFragmentImport(resource);

export interface PartBase {
  id: PartID;
  name: string;
}

interface Part extends PartBase {
  type: typeof PartTypes.MAIN | typeof PartTypes.SUB;
  network: ReadonlyNetwork | undefined;
  resources: ReadonlyResource[];
  fragmentMods: Record<FragmentID, Record<FragmentPrefix, ReadonlyFragmentMod>>;
  otherProps: Record<string, unknown>;
}

interface InvalidSemanticsPart extends PartBase {
  type: typeof PartTypes.MAIN | typeof PartTypes.SUB;
  error: ConfigSemanticError[];
  errorType: typeof ConfigErrorTypes.SEMANTIC;
  config: JsonValue;
}

interface InvalidSyntaxPart extends PartBase {
  type: typeof PartTypes.MAIN | typeof PartTypes.SUB;
  error: ConfigSyntaxError[];
  errorType: typeof ConfigErrorTypes.SYNTAX;
  config: string;
}

type InvalidPart = InvalidSemanticsPart | InvalidSyntaxPart;
type MaybePart = Part | InvalidPart;

export type ReadonlyMaybePart = ReadonlyDeep<MaybePart>;
export type ReadonlyPart = ReadonlyDeep<Part>;
export type ReadonlyInvalidPart = ReadonlyDeep<InvalidPart>;
type ReadonlyInvalidSyntaxPart = ReadonlyDeep<InvalidSyntaxPart>;

export const isPartValid = (
  part: ReadonlyMaybePart | undefined
): part is ReadonlyPart => part !== undefined && !('error' in part);

export const isInvalidPart = (
  part: ReadonlyMaybePart | undefined
): part is ReadonlyInvalidPart => part !== undefined && 'error' in part;

export const isInvalidSyntaxPart = (
  part: ReadonlyMaybePart | undefined
): part is ReadonlyInvalidSyntaxPart =>
  isInvalidPart(part) && part.errorType === ConfigErrorTypes.SYNTAX;

export const getResourcesByType = <
  R extends ReadonlyResource = ReadonlyResource,
>(
  part: ReadonlyPart,
  resourceType: R['type']
) => {
  return part.resources.filter(
    (resource): resource is R => resource.type === resourceType
  );
};

export type FragmentConfig = ReadonlyDeep<
  ResourceParent & {
    description: string | undefined;
    otherProps: Record<string, unknown>;
  }
>;

// TODO: this is a storage type and should live in machine-config.ts or elsewhere
export interface ResourceParent {
  agent: ReadonlyAgent | undefined;
  billing: ReadonlyBilling | undefined;
  network: ReadonlyNetwork | undefined;
  maintenance: ReadonlyMaintenance | undefined;
  components: Record<ResourceID, ReadonlyComponent>;
  services: Record<ResourceID, ReadonlyService>;
  registryModules: Record<ResourceID, ReadonlyRegistryModule>;
  localModules: Record<ResourceID, ReadonlyLocalModule>;
  remotes: Record<ResourceID, ReadonlyRemote>;
  processes: Record<ResourceID, ReadonlyProcess>;
  packages: Record<ResourceID, ReadonlyPackage>;
  triggers: Record<ResourceID, ReadonlyTrigger>;
  jobs: Record<ResourceID, ReadonlyJob>;
  fragmentImports: Record<ResourceID, ReadonlyFragmentImport>;
}

export type ReadonlyResourceParent = ReadonlyDeep<ResourceParent>;

interface Agent extends SingletonResourceBase {
  type: typeof SingletonResourceTypes.AGENT;
  versionControl?: Attributes;
  advancedSettings?: Attributes;
  networkConfiguration?: Attributes;
  additionalNetworks?: Record<string, Attributes>;
  systemConfiguration?: Attributes;
}

export type ReleaseChannel = string;

interface ViamAgentConfig {
  releaseChannel?: ReleaseChannel;
  pinVersion?: string;
  pinURL?: string;
  attributes?: Attributes;
  otherProps?: Record<string, unknown>;
}

interface ViamServerConfig {
  releaseChannel?: ReleaseChannel;
  pinVersion?: string;
  pinURL?: string;
  fastStart?: boolean;
  attributes?: Attributes;
  otherProps?: Record<string, unknown>;
}

interface AgentNetworkConfig {
  type: string;
  ssid?: string;
  psk?: string;
  priority?: number;
}

interface AgentProvisioningConfig {
  roamingMode?: boolean;
  hotspotPassword?: string;
  networks?: AgentNetworkConfig[];
  attributes?: Attributes;
  otherProps?: Record<string, unknown>;
}

export type ReadonlyAgent = ReadonlyDeep<Agent>;

export type ReadonlyViamAgentConfig = ReadonlyDeep<ViamAgentConfig>;

export type ReadonlyViamServerConfig = ReadonlyDeep<ViamServerConfig>;

export type ReadonlyAgentProvisioningConfig =
  ReadonlyDeep<AgentProvisioningConfig>;

export const EmptyAgent: ReadonlyAgent = {
  type: SingletonResourceTypes.AGENT,
  otherProps: {},
};

interface Network extends SingletonResourceBase {
  type: typeof SingletonResourceTypes.NETWORK;
  bindAddress?: string | undefined;
  // heartbeat window seconds
  heartbeatWindowS?: number | undefined;
}

export type ReadonlyNetwork = ReadonlyDeep<Network>;

export const EmptyNetwork: ReadonlyNetwork = {
  type: SingletonResourceTypes.NETWORK,
  otherProps: {},
};

interface Maintenance extends SingletonResourceBase {
  type: typeof SingletonResourceTypes.MAINTENANCE;
  maintenanceAllowedKey?: string | undefined;
  sensorName?: string | undefined;
}

export type ReadonlyMaintenance = ReadonlyDeep<Maintenance>;

export const EmptyMaintenance: ReadonlyMaintenance = {
  type: SingletonResourceTypes.MAINTENANCE,
  otherProps: {},
};

interface Billing extends SingletonResourceBase {
  type: typeof SingletonResourceTypes.BILLING;
}

export type ReadonlyBilling = ReadonlyDeep<Billing>;

export const EmptyBilling: ReadonlyBilling = {
  type: SingletonResourceTypes.BILLING,
  otherProps: {},
};

export type ReadonlySingletonResource =
  | ReadonlyAgent
  | ReadonlyMaintenance
  | ReadonlyNetwork
  | ReadonlyBilling;

export interface SingletonResourceBase {
  /** Props in the resource's config that the UI should pass through. */
  otherProps: Record<string, unknown>;
}

export interface ResourceBase {
  /** The unique ID of the resource, generated on the client side if needed. */
  id: ResourceID;
  /** Props in the resource's config that the UI should pass through. */
  otherProps: Record<string, unknown>;
  /** The folder this resource is under. */
  uiFolder?: UIFolder;
}

export interface NamedResourceBase extends ResourceBase {
  /** The unique name of the resource. */
  name: string;
  notes?: Notes | undefined;
}

export interface DisableAllowed {
  /** Whether the resource is disabled. */
  isDisabled?: boolean | undefined;
}

interface LogConfiguration {
  level?: ResourceLogLevel | undefined;
}

export interface LogConfigurationAllowed {
  /** The logs configuration for a resource. */
  logConfiguration: LogConfiguration | undefined | null;
}

export interface UIFolder {
  /** The name of the folder. */
  name: string;
}

export type Attributes = Readonly<Record<string, unknown>>;

interface CommonResource
  extends NamedResourceBase,
    DisableAllowed,
    LogConfigurationAllowed {
  /** The fully qualified API type of the resource. */
  api: APITriplet;
  /** The fully qualified model. */
  model: ModelTriplet;
  /** Resource configuration attributes. */
  attributes: Attributes;
  /** The names of other resources this resource depends on. */
  dependsOn: string[];
}

interface Component extends CommonResource {
  type: typeof ResourceTypes.COMPONENT;
  frame: Frame | undefined;
  dataManager: DataManager | undefined;
}

export const AssociatedServiceTypes = {
  DATA_MANAGER: 'data_manager',
} as const;

export type AssociatedServiceType = ValueOf<typeof AssociatedServiceTypes>;

interface DataManager {
  type: typeof AssociatedServiceTypes.DATA_MANAGER;
  attributes: {
    captureMethods: CaptureMethod[];
    otherAttributes: Attributes;
  };
  otherProps: Record<string, unknown>;
}

interface CaptureMethod {
  method: CaptureMethodName;
  captureFrequencyHz: number;
  disabled?: boolean | undefined;
  additionalParams: {
    mimeType?: string | undefined;
    cameraName?: string | undefined;
    otherParams: Record<string, unknown>;
    pinName?: string | undefined;
    readerName?: string | undefined;
    docommandInput?: Record<string, unknown> | undefined;
    filterSourceNames?: readonly string[] | undefined;
  };
  otherProps: Record<string, unknown>;
}

export type ReadonlyComponent = ReadonlyDeep<Component>;

export type ReadonlyDataManager = ReadonlyDeep<DataManager>;
export type ReadonlyCaptureMethod = ReadonlyDeep<CaptureMethod>;

// Currently, data manager config is the only component service config type
export type ReadonlyAssociatedService = ReadonlyDataManager;

export const isNamedResource = (
  resource: Pick<ReadonlyResource, 'id' | 'type'> | undefined
): resource is ReadonlyNamedResource =>
  resource !== undefined && 'name' in resource;

export const isComponent = (
  item: ConfigItem | undefined
): item is ReadonlyComponent => item?.type === ResourceTypes.COMPONENT;

interface Service extends CommonResource {
  type: typeof ResourceTypes.SERVICE;
  dataManager: DataManager | undefined;
}

export type ReadonlyService = ReadonlyDeep<Service>;

export const isService = (
  item: ConfigItem | undefined
): item is ReadonlyService => item?.type === ResourceTypes.SERVICE;

export type ReadonlyCommonResource = ReadonlyComponent | ReadonlyService;

export const isCommonResource = (
  item: ConfigItem | undefined
): item is ReadonlyCommonResource => isComponent(item) || isService(item);

interface RegistryModule extends NamedResourceBase, DisableAllowed {
  type: typeof ResourceTypes.REGISTRY_MODULE;
  moduleID: string;
  version: string;
  reloadPath?: string;
  reloadEnabled?: boolean;
  reloadUser?: string;
  reloadTime?: string;
  reloadAnnotation?: string;
  logLevel?: string;
}

export type ReadonlyRegistryModule = ReadonlyDeep<RegistryModule>;

export const isRegistryModule = (
  item: ConfigItem | undefined
): item is ReadonlyRegistryModule =>
  item?.type === ResourceTypes.REGISTRY_MODULE;

interface LocalModule extends NamedResourceBase, DisableAllowed {
  type: typeof ResourceTypes.LOCAL_MODULE;
  executablePath: string;
  logLevel?: string;
}

export type ReadonlyLocalModule = ReadonlyDeep<LocalModule>;

export const isLocalModule = (
  item: ConfigItem | undefined
): item is ReadonlyLocalModule => item?.type === ResourceTypes.LOCAL_MODULE;

export const isModule = (
  item: ConfigItem | undefined
): item is ReadonlyRegistryModule | ReadonlyLocalModule =>
  isRegistryModule(item) || isLocalModule(item);

export const isDataManagerService = (item: ConfigItem | undefined) => {
  return isService(item) && item.api.subtype === 'data_manager';
};

interface Credentials {
  payload?: string | undefined;
  type?: string | undefined;
  otherProps: Record<string, unknown>;
}

interface Auth {
  credentials?: Credentials;
  entity?: string | undefined;
  otherProps: Record<string, unknown>;
}

interface Remote extends NamedResourceBase, DisableAllowed {
  type: typeof ResourceTypes.REMOTE;
  address?: string;
  prefix?: string;
  insecure?: boolean;
  auth?: Auth;
  frame: Frame | undefined;
  otherProps: Record<string, unknown>;
}

export type ReadonlyRemote = ReadonlyDeep<Remote>;
export type ReadonlyRemoteAuth = ReadonlyDeep<Auth>;
export type ReadonlyRemoteAuthCredentials = ReadonlyDeep<Credentials>;

export const isRemote = (
  item: ConfigItem | undefined
): item is ReadonlyRemote => item?.type === ResourceTypes.REMOTE;

interface Process extends NamedResourceBase, DisableAllowed {
  type: typeof ResourceTypes.PROCESS;
  executable?: string;
  args?: string[];
  cwd?: string;
  log?: boolean;
  oneShot?: boolean;
}

export type ReadonlyProcess = ReadonlyDeep<Process>;

export const isProcess = (
  item: ConfigItem | undefined
): item is ReadonlyProcess => item?.type === ResourceTypes.PROCESS;

interface Package extends NamedResourceBase, DisableAllowed {
  type: typeof ResourceTypes.PACKAGE;
  packageID: string;
  version: string | undefined;
  packageType: string;
}

export type ReadonlyPackage = ReadonlyDeep<Package>;

export const isPackage = (
  item: ConfigItem | undefined
): item is ReadonlyPackage => item?.type === ResourceTypes.PACKAGE;

/**
 * FragmentImportTypes describe how the fragment import is stored in the config.
 * ex: "fragment-id" or { id: "fragment-id", version: "1" }
 */
export const FragmentImportTypes = {
  STRING: 'string',
  OBJECT: 'object',
} as const;

type FragmentImportType = ValueOf<typeof FragmentImportTypes>;

interface FragmentImport extends ResourceBase, DisableAllowed {
  type: typeof ResourceTypes.FRAGMENT_IMPORT;
  fragmentID: FragmentID;
  version?: string | undefined;
  importType: FragmentImportType;
  variables?: Record<string, unknown>;
  prefix?: string | undefined;
}

export type ReadonlyFragmentImport = ReadonlyDeep<FragmentImport>;

export type VariablePathMap = Readonly<Record<string, string>>;

/** Filter a VariablePathMap to entries whose key starts with `prefix`, stripping the prefix from each key. */
export const filterVariablePathsByPrefix = (
  paths: Record<string, string>,
  prefix: string
): VariablePathMap =>
  Object.fromEntries(
    Object.entries(paths)
      .filter(([path]) => path.startsWith(prefix))
      .map(([path, varName]) => [path.slice(prefix.length), varName])
  );

interface VariablePaths {
  resourceSection: string;
  resourceName: string;
  paths: VariablePathMap;
}

interface Fragment {
  id: FragmentID;
  version?: string | undefined;
  name: string;
  organizationName: string;
  // publicNamespace is the owning org's public namespace, when it has one.
  publicNamespace?: string | undefined;
  lastUpdated?: Timestamp | undefined;
  originalConfig: FragmentConfig;
  effectiveConfig: FragmentConfig;
  errors: ConfigSemanticError[];
  prefix?: string | undefined;
  variablePaths?: VariablePaths[];
}

export type ReadonlyFragment = ReadonlyDeep<Fragment>;

export const isFragmentImport = (
  item: ConfigItem | undefined
): item is ReadonlyFragmentImport =>
  item?.type === ResourceTypes.FRAGMENT_IMPORT;

export type EvalValueInternal = string | number | boolean;

// EvalValue is recursive in reality, but type utilities like ReadonlyDeep don't
// work with recursive types, so we use `any` here after one level of depth.
export type EvalValue =
  | EvalValueInternal
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | Record<string, any>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  | any[]
  | undefined;

export type RegexValue = string | Record<string, string>;

export const ComparatorOperators = {
  LESS_THAN: 'lt',
  LESS_THAN_OR_EQUAL: 'lte',
  GREATER_THAN: 'gt',
  GREATER_THAN_OR_EQUAL: 'gte',
  EQUAL: 'eq',
  NOT_EQUAL: 'neq',
} as const;

export type ComparatorOperator = ValueOf<typeof ComparatorOperators>;

export interface ComparatorEval {
  operator: ComparatorOperator;
  value?: EvalValue;
}

export const RegexOperator = 'regex';
export interface RegexEval {
  operator: typeof RegexOperator;
  value?: RegexValue;
}

export interface UnknownEval {
  operator?: string;
  value?: RegexValue | EvalValue;
}

export type KnownEval = ComparatorEval | RegexEval;
export type TriggerEval = (KnownEval | UnknownEval) & {
  otherProps?: Record<string, unknown>;
};

export type KnownEvalOperator = KnownEval['operator'];
export type UnknownEvalOperator = UnknownEval['operator'];

export type TriggerEvent =
  | {
      type: TriggerTypesMap['PART_ONLINE'];
    }
  | {
      type: TriggerTypesMap['PART_OFFLINE'];
    }
  | {
      type: TriggerTypesMap['PART_DATA_INGESTED'];
      dataIngested: {
        dataTypes: string[];
      };
    }
  | {
      type: TriggerTypesMap['CONDITIONAL_DATA_INGESTED'];
      dataCapture: {
        componentId: string;
        method: string;
      };
      unparsedDataCapture: string;
      evals: TriggerEval[];
    }
  | {
      type: TriggerTypesMap['CONDITIONAL_LOGS_INGESTED'];
      logLevels: string[];
      logMessageRegex?: string;
    }
  | {
      type: TriggerTypesMap['UNKNOWN'];
      configType: string;
    };

export interface TriggerNotificationBasicAuth {
  username: string;
  password: string;
}
export interface TriggerNotificationAuth {
  basic?: TriggerNotificationBasicAuth;
}
export interface TriggerWebhookNotification {
  type: 'webhook';
  value: string;
  secondsBetweenNotifications: number;
  auth?: TriggerNotificationAuth;
}
export interface TriggerEmailNotification {
  type: 'email';
  value: string;
  secondsBetweenNotifications: number;
}
export interface TriggerPushNotification {
  type: 'push';
  value: string;
  application: string;
  secondsBetweenNotifications: number;
}
export interface TriggerDoCommandNotification {
  type: 'do_command';
  // the name of a resource on the trigger's own part
  value: string;
  command: Record<string, unknown>;
  secondsBetweenNotifications: number;
}
export type TriggerNotification =
  | TriggerWebhookNotification
  | TriggerEmailNotification
  | TriggerPushNotification
  | TriggerDoCommandNotification;

export interface Trigger extends NamedResourceBase, DisableAllowed {
  type: typeof ResourceTypes.TRIGGER;
  event: TriggerEvent;
  notifications: TriggerNotification[];
}

export type ReadonlyTriggerNotification = ReadonlyDeep<TriggerNotification>;
export type ReadonlyTriggerEvent = ReadonlyDeep<TriggerEvent>;
export type ReadonlyTriggerEval = ReadonlyDeep<TriggerEval>;
export type ReadonlyTrigger = ReadonlyDeep<Trigger>;

export const isTrigger = (
  item: ConfigItem | undefined
): item is ReadonlyTrigger => item?.type === ResourceTypes.TRIGGER;

export const isComparatorOperator = (op: string): op is ComparatorOperator => {
  return Object.values<string>(ComparatorOperators).includes(op);
};

export interface Job extends NamedResourceBase, DisableAllowed {
  type: typeof ResourceTypes.JOB;
  schedule?: string;
  resource?: string;
  method?: string;
  command?: Record<string, unknown>;
  logConfiguration?: LogConfiguration | null | undefined;
}

export type ReadonlyJob = ReadonlyDeep<Job>;

export const isJob = (item: ConfigItem | undefined): item is ReadonlyJob =>
  item?.type === ResourceTypes.JOB;

export const isValidRegexVal = (
  val: RegexValue | EvalValue
): val is RegexValue => {
  if (typeof val === 'string') {
    return true;
  }

  if (typeof val !== 'object') {
    return false;
  }

  if (Array.isArray(val)) {
    return false;
  }

  for (const key in val) {
    if (typeof key !== 'string' || typeof val[key] !== 'string') {
      return false;
    }
  }

  return true;
};

export const isKnownOperator = (op: string): op is KnownEvalOperator => {
  return op === RegexOperator || isComparatorOperator(op);
};

export interface OverwriteOperationArgument {
  fieldPath: string;
  value: unknown;
}

export const SupportedOverwriteOperators = [
  MingoUpdateOperators.SET,
  MingoUpdateOperators.UNSET,
] as const;

export type OverwriteOperator = (typeof SupportedOverwriteOperators)[number];

interface OverwriteOperation {
  operator: OverwriteOperator;
  arguments: OverwriteOperationArgument[];
}
export type ReadonlyOverwriteOperation = ReadonlyDeep<OverwriteOperation>;

interface FragmentMod {
  fragmentID: string;
  prefix?: string;
  mods: OverwriteOperation[];
}

export type ReadonlyFragmentMod = ReadonlyDeep<FragmentMod>;

export const createMachineSettingsResourceID = (pID: PartID): ResourceID => {
  return `${pID}/machine-settings`;
};

export const createAccessPolicyResourceID = (pID: PartID): ResourceID =>
  `${pID}/access-policy`;
